/**
 * referralCapture — the sessionStorage transport that survives the Cognito
 * Hosted-UI OAuth full-page redirect.
 *
 * Implements google-sign-in/US-005 (T3) / AC-005.1, BR-005.1, DEC-4.
 *
 * DESIGN RATIONALE: the federated Google door leaves the SPA entirely during
 * the Hosted-UI redirect, so neither React state nor OAuth 'state' (owned by
 * Amplify) can carry the referral code across the round trip. sessionStorage
 * is the ONLY same-origin, same-tab transport that survives a full-page
 * navigation and returns intact — hence this dedicated capture/read-clear
 * module rather than any URL-state or Amplify-state trick.
 *
 * Both functions are NEVER allowed to throw (wrapped in try/catch) — a
 * disabled/unavailable sessionStorage (e.g. private browsing mode) must fall
 * back to "no captured code", never break the OAuth redirect or the
 * callback flow (BR-005.1 / FR-9.5: signup must never be blocked by referral).
 */

// Single source of truth for the sessionStorage key — T4/T5 import this
// constant rather than hardcoding the string.
export const REFERRAL_CAPTURE_KEY = 'pendingReferralCode';

/**
 * Capture a referral code into sessionStorage BEFORE the OAuth redirect
 * fires. Prefers an explicitly-typed field value (the parent edited the
 * referral field); falls back to the `?ref=` URL query param (AC-005.1's
 * two atomic sub-cases: manually-typed AND URL-supplied).
 *
 * Implements US-005 (T3); AC-005.1. Only writes when a non-empty code
 * resolves — never persists an empty string.
 *
 * @param explicitCode optional typed field value (e.g. RegisterPage's
 *   referralCode state); takes precedence over the URL fallback.
 */
export function captureReferralForOAuth(explicitCode?: string): void {
  try {
    const typed = explicitCode?.trim();
    const urlRef =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('ref')?.trim()
        : undefined;
    const code = typed || urlRef || '';

    if (code) {
      window.sessionStorage.setItem(REFERRAL_CAPTURE_KEY, code);
    }
  } catch (e) {
    // Storage unavailable/disabled (private mode) — silent no-op. Signup
    // must never be blocked by a referral-capture failure (BR-005.1).
    console.warn('[referralCapture] captureReferralForOAuth failed (non-blocking)', e);
  }
}

/**
 * Read the captured referral code (if any) and clear it, atomically, so a
 * later manual sign-in can never re-fire a stale code (AC-005.3
 * belt-and-braces on top of the backend ledger dedup).
 *
 * Implements US-005 (T3); AC-005.1, AC-005.4. Always removes the key —
 * even when the stored value is blank — so a stale entry can't linger.
 *
 * @returns the trimmed captured code, or null when absent/blank/unavailable.
 */
export function readAndClearCapturedReferral(): string | null {
  try {
    const value = window.sessionStorage.getItem(REFERRAL_CAPTURE_KEY);
    window.sessionStorage.removeItem(REFERRAL_CAPTURE_KEY);
    return value && value.trim() ? value.trim() : null;
  } catch (e) {
    console.warn('[referralCapture] readAndClearCapturedReferral failed (non-blocking)', e);
    return null;
  }
}

/**
 * BUG-20260713-020205 (D2, Option B) / T-5: OAuth login-vs-signup INTENT
 * transport. Mirrors REFERRAL_CAPTURE_KEY's sessionStorage capture/read-clear
 * contract exactly (guarded, never-throw, survives the full-page Hosted-UI
 * redirect) but carries a distinct signal: which entry point
 * (LoginPage vs RegisterPage) initiated the Google OAuth redirect, so
 * OAuthCallbackPage can enforce "login-page Google is sign-in-only" (Option
 * B) post-callback. Uses a DIFFERENT sessionStorage key from
 * REFERRAL_CAPTURE_KEY — the two transports are independent and must never
 * collide. - 2026-07-13
 */
export const OAUTH_INTENT_CAPTURE_KEY = 'pendingOAuthIntent';

export type OAuthIntent = 'login' | 'signup';

/**
 * Capture the login-vs-signup intent into sessionStorage BEFORE the OAuth
 * redirect fires. Never throws — an unavailable/disabled sessionStorage
 * (private browsing) must never block the redirect (mirrors
 * captureReferralForOAuth's contract; BR-005.1-equivalent for D2).
 *
 * @param intent 'login' (LoginPage's Google button) or 'signup' (every
 *   RegisterPage Google button usage).
 */
export function captureOAuthIntent(intent: OAuthIntent): void {
  try {
    window.sessionStorage.setItem(OAUTH_INTENT_CAPTURE_KEY, intent);
  } catch (e) {
    console.warn('[referralCapture] captureOAuthIntent failed (non-blocking)', e);
  }
}

/**
 * Read the captured OAuth intent (if any) and clear it, atomically, so a
 * stale marker can never linger for a later sign-in. Always removes the key,
 * even when the stored value is unrecognised. Never throws.
 *
 * @returns 'login' | 'signup' when a recognised value was captured, or null
 *   when absent/unrecognised/unavailable (callers must treat null as
 *   'signup' — i.e. never bounce — per the fix plan's fail-safe default).
 */
export function readAndClearOAuthIntent(): OAuthIntent | null {
  try {
    const value = window.sessionStorage.getItem(OAUTH_INTENT_CAPTURE_KEY);
    window.sessionStorage.removeItem(OAUTH_INTENT_CAPTURE_KEY);
    return value === 'login' || value === 'signup' ? value : null;
  } catch (e) {
    console.warn('[referralCapture] readAndClearOAuthIntent failed (non-blocking)', e);
    return null;
  }
}

/**
 * consent-on-google-signup: T&C/Privacy consent transport. Mirrors the
 * referral/intent sessionStorage capture-read-clear contract EXACTLY (guarded,
 * never-throw, survives the full-page Hosted-UI redirect) but carries the
 * parent's affirmative Terms + Privacy acceptance made on the RegisterPage
 * checkbox BEFORE the Google redirect. OAuthCallbackPage reads-and-clears it
 * after provisioning and writes the consent record via POST /auth/consent —
 * collapsing the post-login consent modal into the register-page checkbox for
 * the Google signup door. Uses a DISTINCT sessionStorage key; independent of
 * the referral and intent transports (they must never collide). The
 * server-side require_consent() gate remains the sole authority — this only
 * pre-satisfies it for a fresh signup so the modal never has to appear.
 */
export const CONSENT_CAPTURE_KEY = 'pendingTncAccepted';

/**
 * Capture the parent's affirmative T&C/Privacy acceptance into sessionStorage
 * BEFORE the OAuth redirect fires. Only ever persists an affirmative (true)
 * acceptance — never writes a negative/false marker. Never throws (an
 * unavailable/disabled sessionStorage must never block the redirect; the
 * server gate + post-login modal stay as the fail-closed fallback).
 *
 * @param accepted the RegisterPage tncChecked value at click time. The signup
 *   Google button is disabled until this is true, so in practice it is always
 *   true here — the guard is defensive.
 */
export function captureConsentForOAuth(accepted: boolean): void {
  try {
    if (accepted === true) {
      window.sessionStorage.setItem(CONSENT_CAPTURE_KEY, 'true');
    }
  } catch (e) {
    console.warn('[referralCapture] captureConsentForOAuth failed (non-blocking)', e);
  }
}

/**
 * Read the captured T&C acceptance (if any) and clear it, atomically, so a
 * stale marker can never linger for a later sign-in. Always removes the key.
 * Never throws.
 *
 * @returns true ONLY when an affirmative acceptance was captured; false when
 *   absent/negative/unavailable (callers treat false as "not pre-captured" —
 *   the server gate then surfaces the consent modal as the fallback).
 */
export function readAndClearCapturedConsent(): boolean {
  try {
    const value = window.sessionStorage.getItem(CONSENT_CAPTURE_KEY);
    window.sessionStorage.removeItem(CONSENT_CAPTURE_KEY);
    return value === 'true';
  } catch (e) {
    console.warn('[referralCapture] readAndClearCapturedConsent failed (non-blocking)', e);
    return false;
  }
}
